import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Typography, Select, DatePicker, InputNumber, Row, Col, Divider, message, Alert } from 'antd';
import { ClockCircleOutlined, ProjectOutlined, CalendarOutlined, CheckSquareOutlined } from '@ant-design/icons';
import { useTimesheetStore } from '../../store/useTimesheetStore';
import { useAuthStore } from '../../store/useAuthStore';
import ErrorDisplay from '../common/ErrorDisplay';
import TodayTimesheets from './TodayTimesheets';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

const TimesheetForm = () => {
  const [form] = Form.useForm();
  const { user } = useAuthStore();
  const { 
    projects, 
    loading, 
    error, 
    fetchProjects, 
    submitTimesheet, 
    clearError 
  } = useTimesheetStore();
  
  const [validationErrors, setValidationErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [callTodayTimesheets, setCallTodayTimesheets] = useState(false);

  useEffect(() => {
    // Fetch projects based on user role - employees get only assigned projects
    if (user) {
      if (user.role === 'employee' || user.role === 'project_manager') {
        fetchProjects(user.id);
      } else {
        fetchProjects(); // Admin gets all projects
      }
      
      // Set initial form values when user data is available
      form.setFieldsValue({
        employeeName: user.name,
        employeeEmail: user.email,
        date: dayjs(),
      });
    }
  }, [fetchProjects, user, form]);

  // Fetch tasks when project is selected
  const fetchTasks = async (projectId) => {
    if (!projectId) {
      setTasks([]);
      return;
    }

    setLoadingTasks(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tasks/project/${projectId}?status=pending&status=in-progress`);
      if (response.ok) {
        const data = await response.json();
        // Filter tasks assigned to current user
        const userTasks = data.filter(task => task.assignedTo._id === user.id);
        setTasks(userTasks);
      } else {
        console.error('Failed to fetch tasks');
        setTasks([]);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  };

  const handleProjectChange = (projectId) => {
    setSelectedProject(projectId);
    form.setFieldsValue({ task: undefined }); // Clear task selection
    fetchTasks(projectId);
  };

  const handleSubmit = async (values) => {
    setValidationErrors({});
    clearError();
    setSubmitting(true);

    try {
      // Find the selected project to get its name
      const selectedProject = Array.isArray(projects?.data) ? projects?.data.find(p => p._id === values.project) : null;
      
      // Find selected task if provided
      const selectedTask = values.task ? tasks.find(t => t._id === values.task) : null;
      
      const timesheetData = {
        employeeId: user.id,
        employeeName: user.name,
        employeeEmail: user.email,
        projectId: values.project,
        projectName: selectedProject?.name || '',
        taskId: values.task || null,
        taskTitle: selectedTask?.title || '',
        date: values.date.format('YYYY-MM-DD'),
        hours: values.hours,
        description: values.description || '',
      };

      // Update task actual hours if task is selected
      if (values.task && values.hours) {
        try {
          const taskResponse = await fetch(`/api/tasks/${values.task}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              actualHours: (selectedTask?.actualHours || 0) + values.hours
            }),
          });
          
          if (!taskResponse.ok) {
            console.warn('Failed to update task hours');
          }
        } catch (error) {
          console.warn('Error updating task hours:', error);
        }
      }

      const result = await submitTimesheet(timesheetData);
      
      
      if (result.success) {
        form.resetFields();
        // Reset form with user data
        form.setFieldsValue({
          employeeName: user.name,
          employeeEmail: user.email,
          date: dayjs(),
        });
        setTasks([]);
        setSelectedProject(null);
        message.success('Timesheet submitted successfully!');
        setCallTodayTimesheets(true);
      }
    } catch (err) {
      console.error('Timesheet submission error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFormChange = () => {
    setValidationErrors({});
    clearError();
  };

  const disabledDate = (current) => {
    // Disable future dates
    return current && current > dayjs().endOf('day');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={12}>
            <Card className="shadow-lg">
          <div className="text-center mb-6">
            <Title level={2} className="text-gray-900 mb-2">
              <ClockCircleOutlined className="mr-2" />
              Timesheet Entry
            </Title>
            <Text className="text-gray-600">
              Welcome back, {user?.name}! Submit your daily work hours.
            </Text>
          </div>

          {error && (
            <div className="mb-4">
              <ErrorDisplay
                errorCode="SUBMISSION_ERROR"
                errorMessage={error}
                closable
                onClose={clearError}
              />
            </div>
          )}

          <Form
            form={form}
            name="timesheet"
            onFinish={handleSubmit}
            onValuesChange={handleFormChange}
            layout="vertical"
            size="large"
            autoComplete="off"
            initialValues={{
              employeeName: user?.name || '',
              employeeEmail: user?.email || '',
              date: dayjs(),
            }}
          >
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Employee Name"
                  name="employeeName"
                >
                  <Input
                    disabled
                    className="bg-gray-50"
                    aria-label="Employee name (read-only)"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Employee Email"
                  name="employeeEmail"
                >
                  <Input
                    disabled
                    className="bg-gray-50"
                    aria-label="Employee email (read-only)"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            {Array.isArray(projects?.data) && 
             projects?.data.filter(project => project.status !== 'completed' && project.status !== 'cancelled').length === 0 && (
              <Alert
                message="No Active Projects Available"
                description="All your assigned projects are either completed or cancelled. Please contact your project manager or admin to get assigned to an active project."
                type="warning"
                showIcon
                className="mb-4"
              />
            )}

            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Project"
                  name="project"
                  rules={[
                    {
                      required: true,
                      message: 'Please select a project!',
                    },
                  ]}
                  validateStatus={validationErrors.project ? 'error' : ''}
                  help={validationErrors.project}
                >
                  <Select
                    placeholder="Select a project"
                    loading={loading}
                    showSearch
                    filterOption={(input, option) =>
                      (option.label ?? '').toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                    aria-label="Select project"
                    className="w-full"
                    onChange={handleProjectChange}
                  >
                    {Array.isArray(projects?.data) && projects?.data
                      .filter(project => project.status !== 'completed' && project.status !== 'cancelled')
                      .map((project) => (
                      <Option key={project._id} value={project._id} label={project.name}>
                        <ProjectOutlined className="mr-2" />
                        {project.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Date"
                  name="date"
                  rules={[
                    {
                      required: true,
                      message: 'Please select a date!',
                    },
                  ]}
                  validateStatus={validationErrors.date ? 'error' : ''}
                  help={validationErrors.date}
                >
                  <DatePicker
                    className="w-full"
                    format="YYYY-MM-DD"
                    disabledDate={disabledDate}
                    suffixIcon={<CalendarOutlined />}
                    aria-label="Select work date"
                    placeholder="Select date"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Task (Optional)"
                  name="task"
                  validateStatus={validationErrors.task ? 'error' : ''}
                  help={validationErrors.task}
                >
                  <Select
                    placeholder={selectedProject ? "Select a task (optional)" : "Select a project first"}
                    loading={loadingTasks}
                    disabled={!selectedProject || tasks.length === 0}
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                      (option.label ?? '').toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                    aria-label="Select task (optional)"
                    className="w-full"
                  >
                    {tasks.map((task) => (
                      <Option key={task._id} value={task._id} label={task.title}>
                        <CheckSquareOutlined className="mr-2" />
                        {task.title}
                        {task.priority && (
                          <span style={{ 
                            marginLeft: '8px', 
                            fontSize: '12px', 
                            color: task.priority === 'high' ? '#ff4d4f' : task.priority === 'medium' ? '#faad14' : '#52c41a' 
                          }}>
                            ({task.priority})
                          </span>
                        )}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Hours Worked"
                  name="hours"
                  rules={[
                    {
                      required: true,
                      message: 'Please enter hours worked!',
                    },
                    {
                      type: 'number',
                      min: 0.5,
                      max: 8,
                      message: 'Hours must be between 0.5 and 8!',
                    },
                  ]}
                  validateStatus={validationErrors.hours ? 'error' : ''}
                  help={validationErrors.hours}
                >
                  <InputNumber
                    className="w-full"
                    placeholder="Enter hours (e.g., 4.5)"
                    min={0.5}
                    max={8}
                    step={0.5}
                    precision={1}
                    aria-label="Hours worked"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 0]}>
              <Col xs={24}>
                <Form.Item
                  label="Description (Optional)"
                  name="description"
                  validateStatus={validationErrors.description ? 'error' : ''}
                  help={validationErrors.description}
                >
                  <Input.TextArea
                    placeholder="Brief description of work done"
                    rows={4}
                    maxLength={500}
                    showCount
                    aria-label="Work description (optional)"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            <Form.Item className="mb-0">
              <Row gutter={[16, 16]} justify="end">
                <Col xs={24} sm={12} md={12}>
                  <Button
                    type="default"
                    size="large"
                    className="w-full"
                    onClick={() => form.resetFields()}
                    disabled={submitting}
                    aria-label="Clear form"
                  >
                    Clear Form
                  </Button>
                </Col>
                <Col xs={24} sm={12} md={12}>
                  <Button
                    type="primary"
                    size="large"
                    className="w-full"
                    htmlType="submit"
                    loading={submitting}
                    aria-label="Submit timesheet"
                  >
                    Submit Timesheet
                  </Button>
                </Col>
              </Row>
            </Form.Item>
          </Form>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <Text className="text-sm text-blue-800">
              <strong>Note:</strong> All timesheet entries are recorded in the company's Google Sheet. 
              Make sure to submit your hours daily for accurate tracking.
            </Text>
          </div>
            </Card>
          </Col>
          
          <Col xs={24} lg={12}>
            <TodayTimesheets user={user} callTodayTimesheets={callTodayTimesheets} />
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default TimesheetForm;
